import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../../App';
import { Building2, Clock, Check, X, Edit3, User, Filter, ChevronDown, Star, Shield, AlertTriangle, Flag, ClipboardEdit, Trash } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import ManualHoursModal from '../../components/ManualHoursModal';

const STATUS_STYLES = {
  pending: 'bg-amber-50 text-amber-700 border border-amber-200',
  edited: 'bg-blue-50 text-blue-700 border border-blue-200',
  approved: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  rejected: 'bg-red-50 text-red-700 border border-red-200',
};

const LEVEL_LABELS = {
  level_1: 'Level 1 - General',
  level_2: 'Level 2 - Approved',
  level_3: 'Level 3 - Trusted',
};

const TimeEntryApproval = () => {
  const { user, logout } = useContext(AuthContext);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('review'); // review, approved, rejected, all
  const [editModal, setEditModal] = useState(null);
  const [editForm, setEditForm] = useState({ check_in_time: '', check_out_time: '', edit_reason: '' });
  const [levelModal, setLevelModal] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState('level_1');
  const [volunteerClearance, setVolunteerClearance] = useState({});

  // Clearance modal
  const [clearanceModal, setClearanceModal] = useState(null);
  const [clearanceForm, setClearanceForm] = useState({ clearance_level: 'level_1', clearance_status: 'verified', expiration_date: '' });

  // Flag modal
  const [flagModal, setFlagModal] = useState(null);
  const [flagNote, setFlagNote] = useState('');

  // Manual Hours
  const [showManualHours, setShowManualHours] = useState(false);
  const [manualEntries, setManualEntries] = useState([]);
  const fetchManualEntries = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/hours/manual`);
      setManualEntries(res.data || []);
    } catch (err) {
      // ignore
    }
  }, []);
  useEffect(() => { fetchManualEntries(); }, [fetchManualEntries]);
  const handleDeleteManualEntry = async (entry) => {
    if (!window.confirm(`Delete this manual entry of ${entry.credit_hours}h for ${entry.user_name}?\n\nThe participant's total hours will be reduced by ${entry.credit_hours}.`)) return;
    try {
      await axios.delete(`${API}/admin/hours/manual/${entry.id}`);
      toast.success('Manual entry deleted');
      fetchManualEntries();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const res = await axios.get(`${API}/time-entries/org${params}`);
      const data = res.data || [];
      setEntries(data);
      // Fetch clearance for unique volunteers
      const volIds = [...new Set(data.map(e => e.volunteer_id))];
      const clearanceMap = {};
      await Promise.all(volIds.map(async (vid) => {
        try {
          const c = await axios.get(`${API}/volunteer/${vid}/clearance`);
          clearanceMap[vid] = c.data;
        } catch { /* Failed to fetch volunteer clearance */ }
      }));
      setVolunteerClearance(clearanceMap);
    } catch (error) {
      toast.error('Failed to load time entries');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const handleApprove = async (entryId) => {
    try {
      await axios.put(`${API}/time-entries/${entryId}/approve`);
      toast.success('Entry approved');
      fetchEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleReject = async (entryId) => {
    try {
      await axios.put(`${API}/time-entries/${entryId}/reject`);
      toast.success('Entry rejected');
      fetchEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    }
  };

  const handleOrgEdit = async () => {
    if (!editModal || !editForm.edit_reason.trim()) {
      toast.error('Please provide a reason');
      return;
    }
    try {
      const payload = { edit_reason: editForm.edit_reason };
      if (editForm.check_in_time) payload.check_in_time = new Date(editForm.check_in_time).toISOString();
      if (editForm.check_out_time) payload.check_out_time = new Date(editForm.check_out_time).toISOString();
      await axios.put(`${API}/time-entries/${editModal.id}/org-edit`, payload);
      toast.success('Entry updated');
      setEditModal(null);
      fetchEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Edit failed');
    }
  };

  const handleUpdateLevel = async () => {
    if (!levelModal) return;
    try {
      await axios.put(`${API}/volunteer/${levelModal.volunteer_id}/level`, { volunteer_level: selectedLevel });
      toast.success(`Level updated to ${LEVEL_LABELS[selectedLevel]}`);
      setLevelModal(null);
      fetchEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update level');
    }
  };

  const handleAssignClearance = async () => {
    if (!clearanceModal) return;
    try {
      await axios.put(`${API}/volunteer/${clearanceModal.volunteer_id}/clearance`, {
        clearance_level: clearanceForm.clearance_level,
        clearance_status: clearanceForm.clearance_status,
        expiration_date: clearanceForm.expiration_date || null,
      });
      toast.success('Clearance assigned');
      setClearanceModal(null);
      fetchEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign clearance');
    }
  };

  const handleToggleFlag = async () => {
    if (!flagModal) return;
    const vc = volunteerClearance[flagModal.volunteer_id];
    const isActive = vc?.org_alert_status === 'active';
    try {
      await axios.put(`${API}/volunteer/${flagModal.volunteer_id}/flag`, {
        org_alert_status: isActive ? 'resolved' : 'active',
        org_alert_note: isActive ? null : flagNote,
      });
      toast.success(isActive ? 'Alert resolved' : 'Alert placed');
      setFlagModal(null);
      setFlagNote('');
      fetchEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update alert');
    }
  };

  const openEditModal = (entry) => {
    setEditModal(entry);
    setEditForm({
      check_in_time: entry.check_in_time ? format(new Date(entry.check_in_time), "yyyy-MM-dd'T'HH:mm") : '',
      check_out_time: entry.check_out_time ? format(new Date(entry.check_out_time), "yyyy-MM-dd'T'HH:mm") : '',
      edit_reason: '',
    });
  };

  const openLevelModal = (entry) => {
    setLevelModal(entry);
    setSelectedLevel('level_1');
  };

  const formatTime = (isoStr) => {
    if (!isoStr) return '--';
    try { return format(new Date(isoStr), 'MMM d, h:mm a'); } catch { return isoStr; }
  };

  const reviewCount = entries.filter(e => e.status === 'pending' || e.status === 'edited').length;

  return (
    <div className="min-h-screen bg-[#FBF4EA]" data-testid="time-entry-approval-page">
      {/* Navigation */}
      <nav className="bg-white border-b-2 border-[#E9DDCF] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[#8B2E3C]" />
              <span className="font-bold text-[#222]">{user?.partner_org_name || 'Partner Portal'}</span>
            </div>
            <div className="flex items-center gap-3 text-sm overflow-x-auto">
              <Link to="/partner/dashboard" className="text-[#222] hover:text-[#8B2E3C] font-medium whitespace-nowrap">Dashboard</Link>
              <Link to="/partner/opportunities" className="text-[#222] hover:text-[#8B2E3C] font-medium whitespace-nowrap">Opportunities</Link>
              <Link to="/partner/community" className="text-[#222] hover:text-[#8B2E3C] font-medium whitespace-nowrap">Community Board</Link>
              <Link to="/partner/participants" className="text-[#222] hover:text-[#8B2E3C] font-medium whitespace-nowrap">Participants</Link>
              <Link to="/partner/invites" className="text-[#222] hover:text-[#8B2E3C] font-medium whitespace-nowrap">Invites</Link>
              <Link to="/partner/staff" className="text-[#222] hover:text-[#8B2E3C] font-medium whitespace-nowrap">Staff</Link>
              <Link to="/partner/time-entries" className="text-[#8B2E3C] font-bold whitespace-nowrap">Time Entries</Link>
              <Link to="/partner/reports" className="text-[#222] hover:text-[#8B2E3C] font-medium whitespace-nowrap">Reports</Link>
              <button onClick={logout} className="text-[#8B2E3C] hover:underline whitespace-nowrap">Logout</button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 py-6 sm:py-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#222]">Time Entry Approval</h1>
            <p className="text-sm text-[#666]">Review and approve volunteer hours</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowManualHours(true)}
              className="bg-white border-2 border-[#8B2E3C] text-[#8B2E3C] px-4 py-2 rounded-lg font-medium hover:bg-[#FBF4EA] flex items-center gap-2 text-sm"
              data-testid="add-manual-hours-button"
            >
              <ClipboardEdit className="w-4 h-4" />
              Add Manual Hours
            </button>
            {filter === 'review' && (
              <div className="bg-[#8B2E3C] text-white px-4 py-2 rounded-lg font-bold text-lg" data-testid="pending-count">
                {reviewCount} to review
              </div>
            )}
          </div>
        </div>

        {/* Manual Entries section (this org only) */}
        {manualEntries.length > 0 && (
          <div className="mb-6">
            <h2 className="text-lg font-bold text-[#222] mb-3">Manual Entries ({manualEntries.length})</h2>
            <div className="grid gap-3" data-testid="manual-entries-list">
              {manualEntries.map((m) => (
                <div key={m.id} className="bg-white rounded-lg shadow-sm border-l-4 border-l-blue-500 p-4" data-testid={`manual-entry-${m.id}`}>
                  <div className="flex flex-col sm:flex-row justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <h4 className="font-bold text-[#222]">{m.opportunity_title}</h4>
                        <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-blue-100 text-blue-800" data-testid="manual-entry-badge">Manual Entry</span>
                      </div>
                      <p className="text-sm text-[#666]">{m.user_name} ({m.user_email})</p>
                      <div className="text-sm text-[#666] mt-1 flex flex-wrap gap-4">
                        <span>{m.opportunity_date}</span>
                        <span><strong>{m.credit_hours}</strong> hours credited</span>
                      </div>
                      <p className="text-xs text-[#888] mt-1.5 italic">Reason: {m.reason}</p>
                      <p className="text-xs text-[#888] mt-0.5">
                        Entered by {m.entered_by_name || m.entered_by_email} ({m.entered_by_role}) on {m.entered_at ? format(new Date(m.entered_at), 'MMM d, yyyy h:mm a') : ''}
                      </p>
                    </div>
                    <button
                      onClick={() => handleDeleteManualEntry(m)}
                      className="self-start p-2 text-red-600 hover:bg-red-50 rounded-lg shrink-0"
                      title="Delete manual entry"
                      data-testid={`delete-manual-entry-${m.id}`}
                    >
                      <Trash className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filter tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
          {[
            { key: 'review', label: 'Needs Review' },
            { key: 'approved', label: 'Approved' },
            { key: 'rejected', label: 'Rejected' },
            { key: 'all', label: 'All' },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors whitespace-nowrap ${
                filter === tab.key
                  ? 'bg-[#8B2E3C] text-white'
                  : 'bg-white border-2 border-[#E9DDCF] text-[#222] hover:border-[#8B2E3C]'
              }`}
              data-testid={`filter-${tab.key}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Entries */}
        {loading ? (
          <div className="text-center py-12 text-[#888]">Loading entries...</div>
        ) : entries.length === 0 ? (
          <div className="card text-center py-10">
            <Clock className="w-10 h-10 text-[#ccc] mx-auto mb-3" />
            <p className="text-[#888]">
              {filter === 'review' ? 'No entries to review' : 'No entries found'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {entries.map(entry => {
              const vc = volunteerClearance[entry.volunteer_id];
              const clearanceLabels = {level_1: 'Community', level_2: 'Youth', level_3: 'Vulnerable Pop.'};
              const hasAlert = vc?.org_alert_status === 'active';
              return (
              <div key={entry.id} className={`card ${hasAlert ? 'border-l-4 border-l-red-500' : ''}`} data-testid={`org-entry-${entry.id}`}>
                <div className="flex flex-col gap-3">
                  {/* Org Alert Warning */}
                  {hasAlert && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-2 flex items-start gap-2" data-testid={`alert-warning-${entry.id}`}>
                      <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
                      <div className="text-xs text-red-700">
                        <span className="font-semibold">Active Alert</span> — Please contact {vc?.org_alert_org_name || 'reporting org'} before approving.
                        {vc?.org_alert_note && <span className="block text-red-600 mt-0.5">{vc.org_alert_note}</span>}
                      </div>
                    </div>
                  )}

                  {/* Header */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0 flex-wrap">
                      <User className="w-5 h-5 text-[#8B2E3C] shrink-0" />
                      <span className="font-semibold text-[#222] truncate">{entry.volunteer_name}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium shrink-0 ${STATUS_STYLES[entry.status]}`}>
                        {entry.status}
                      </span>
                      {vc?.clearance_level && (
                        <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-purple-50 text-purple-700 border border-purple-200 shrink-0" data-testid={`clearance-badge-${entry.id}`}>
                          <Shield className="w-3 h-3 inline mr-0.5" />
                          {clearanceLabels[vc.clearance_level] || 'L1'}
                          {vc.clearance_status === 'expired' && ' (Expired)'}
                        </span>
                      )}
                    </div>
                    <span className="text-xl font-bold text-[#8B2E3C] shrink-0">
                      {entry.total_hours != null ? `${entry.total_hours.toFixed(1)}h` : '--'}
                    </span>
                  </div>

                  {/* Details */}
                  <div className="text-sm text-[#666] space-y-0.5 ml-7">
                    {entry.opportunity_title && (
                      <div className="text-xs text-[#888]">{entry.opportunity_title}</div>
                    )}
                    <div>In: {formatTime(entry.check_in_time)}</div>
                    <div>Out: {formatTime(entry.check_out_time)}</div>
                    <div className="text-xs text-[#999]">{entry.volunteer_email}</div>
                  </div>

                  {entry.edited_flag && entry.edit_reason && (
                    <div className="text-xs text-blue-600 ml-7 flex items-center gap-1">
                      <Edit3 className="w-3 h-3" /> Edit note: {entry.edit_reason}
                    </div>
                  )}

                  {/* Actions */}
                  {(entry.status === 'pending' || entry.status === 'edited') && (
                    <div className="flex flex-wrap gap-2 ml-7 pt-1">
                      <button
                        onClick={() => handleApprove(entry.id)}
                        className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2.5 rounded-lg font-medium text-sm transition-colors active:scale-95"
                        data-testid={`approve-${entry.id}`}
                      >
                        <Check className="w-4 h-4" /> Approve
                      </button>
                      <button
                        onClick={() => openEditModal(entry)}
                        className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg font-medium text-sm transition-colors active:scale-95"
                        data-testid={`edit-${entry.id}`}
                      >
                        <Edit3 className="w-4 h-4" /> Edit
                      </button>
                      <button
                        onClick={() => handleReject(entry.id)}
                        className="flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white px-4 py-2.5 rounded-lg font-medium text-sm transition-colors active:scale-95"
                        data-testid={`reject-${entry.id}`}
                      >
                        <X className="w-4 h-4" /> Reject
                      </button>
                      <button
                        onClick={() => openLevelModal(entry)}
                        className="flex items-center gap-1.5 bg-[#C9A227] hover:bg-[#a8861f] text-[#222] px-4 py-2.5 rounded-lg font-medium text-sm transition-colors active:scale-95"
                        data-testid={`level-${entry.id}`}
                      >
                        <Star className="w-4 h-4" /> Set Level
                      </button>
                      <button
                        onClick={() => { setClearanceModal(entry); setClearanceForm({ clearance_level: vc?.clearance_level || 'level_1', clearance_status: 'verified', expiration_date: '' }); }}
                        className="flex items-center gap-1.5 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2.5 rounded-lg font-medium text-sm transition-colors active:scale-95"
                        data-testid={`clearance-${entry.id}`}
                      >
                        <Shield className="w-4 h-4" /> Clearance
                      </button>
                      <button
                        onClick={() => { setFlagModal(entry); setFlagNote(''); }}
                        className={`flex items-center gap-1.5 px-4 py-2.5 rounded-lg font-medium text-sm transition-colors active:scale-95 ${
                          hasAlert ? 'bg-gray-600 hover:bg-gray-700 text-white' : 'bg-red-100 hover:bg-red-200 text-red-700'
                        }`}
                        data-testid={`flag-${entry.id}`}
                      >
                        <Flag className="w-4 h-4" /> {hasAlert ? 'Resolve Alert' : 'Alert'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ===== EDIT MODAL ===== */}
      {editModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="org-edit-modal">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-[#222] mb-4">Edit Time Entry</h3>
            <p className="text-sm text-[#666] mb-4">Volunteer: {editModal.volunteer_name}</p>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-[#444] mb-1">Check-In Time</label>
                <input
                  type="datetime-local"
                  value={editForm.check_in_time}
                  onChange={(e) => setEditForm(f => ({ ...f, check_in_time: e.target.value }))}
                  className="input-field"
                  data-testid="org-edit-check-in"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#444] mb-1">Check-Out Time</label>
                <input
                  type="datetime-local"
                  value={editForm.check_out_time}
                  onChange={(e) => setEditForm(f => ({ ...f, check_out_time: e.target.value }))}
                  className="input-field"
                  data-testid="org-edit-check-out"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#444] mb-1">Reason *</label>
                <textarea
                  value={editForm.edit_reason}
                  onChange={(e) => setEditForm(f => ({ ...f, edit_reason: e.target.value }))}
                  className="input-field min-h-[80px]"
                  placeholder="Reason for the correction..."
                  data-testid="org-edit-reason"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={handleOrgEdit} className="flex-1 btn-primary py-3" data-testid="org-edit-submit">
                  Save Changes
                </button>
                <button
                  onClick={() => setEditModal(null)}
                  className="px-6 py-3 rounded-lg border-2 border-[#E9DDCF] text-[#666] font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== LEVEL MODAL ===== */}
      {levelModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="level-modal">
          <div className="bg-white rounded-xl max-w-sm w-full p-6">
            <h3 className="text-lg font-bold text-[#222] mb-2">Assign Volunteer Level</h3>
            <p className="text-sm text-[#666] mb-4">{levelModal.volunteer_name}</p>
            <div className="space-y-2 mb-4">
              {Object.entries(LEVEL_LABELS).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setSelectedLevel(key)}
                  className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-colors font-medium ${
                    selectedLevel === key
                      ? 'border-[#8B2E3C] bg-[#8B2E3C]/5 text-[#8B2E3C]'
                      : 'border-[#E9DDCF] text-[#444] hover:border-[#8B2E3C]'
                  }`}
                  data-testid={`level-option-${key}`}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="flex gap-3">
              <button onClick={handleUpdateLevel} className="flex-1 btn-primary py-3" data-testid="level-submit">
                Assign Level
              </button>
              <button
                onClick={() => setLevelModal(null)}
                className="px-6 py-3 rounded-lg border-2 border-[#E9DDCF] text-[#666] font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== CLEARANCE MODAL ===== */}
      {clearanceModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="clearance-modal">
          <div className="bg-white rounded-xl max-w-sm w-full p-6">
            <h3 className="text-lg font-bold text-[#222] mb-2">Assign Clearance Level</h3>
            <p className="text-sm text-[#666] mb-4">{clearanceModal.volunteer_name}</p>
            <div className="space-y-3 mb-4">
              <div>
                <label className="block text-sm font-medium text-[#444] mb-1">Clearance Level</label>
                <select
                  value={clearanceForm.clearance_level}
                  onChange={(e) => setClearanceForm(f => ({ ...f, clearance_level: e.target.value }))}
                  className="input-field"
                  data-testid="clearance-level-select"
                >
                  <option value="level_1">Level 1 - Community Volunteer</option>
                  <option value="level_2">Level 2 - Youth Volunteer</option>
                  <option value="level_3">Level 3 - Vulnerable Population</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#444] mb-1">Status</label>
                <select
                  value={clearanceForm.clearance_status}
                  onChange={(e) => setClearanceForm(f => ({ ...f, clearance_status: e.target.value }))}
                  className="input-field"
                  data-testid="clearance-status-select"
                >
                  <option value="verified">Verified</option>
                  <option value="in_progress">In Progress</option>
                  <option value="under_review">Under Review</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#444] mb-1">Expiration Date</label>
                <input
                  type="date"
                  value={clearanceForm.expiration_date}
                  onChange={(e) => setClearanceForm(f => ({ ...f, expiration_date: e.target.value }))}
                  className="input-field"
                  data-testid="clearance-expiration-input"
                />
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={handleAssignClearance} className="flex-1 btn-primary py-3" data-testid="clearance-submit">
                Assign Clearance
              </button>
              <button
                onClick={() => setClearanceModal(null)}
                className="px-6 py-3 rounded-lg border-2 border-[#E9DDCF] text-[#666] font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== ALERT MODAL ===== */}
      {flagModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="flag-modal">
          <div className="bg-white rounded-xl max-w-sm w-full p-6">
            {volunteerClearance[flagModal.volunteer_id]?.org_alert_status === 'active' ? (
              <>
                <h3 className="text-lg font-bold text-[#222] mb-2">Resolve Alert</h3>
                <p className="text-sm text-[#666] mb-4">Resolve the active alert on {flagModal.volunteer_name}?</p>
                <div className="flex gap-3">
                  <button onClick={handleToggleFlag} className="flex-1 bg-gray-600 hover:bg-gray-700 text-white py-3 rounded-lg font-medium" data-testid="flag-remove-submit">
                    Resolve Alert
                  </button>
                  <button onClick={() => setFlagModal(null)} className="px-6 py-3 rounded-lg border-2 border-[#E9DDCF] text-[#666] font-medium">Cancel</button>
                </div>
              </>
            ) : (
              <>
                <h3 className="text-lg font-bold text-[#222] mb-2">Place Organizational Alert</h3>
                <p className="text-sm text-[#666] mb-4">Alert other organizations about {flagModal.volunteer_name}</p>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-[#444] mb-1">Note (visible to other orgs)</label>
                  <textarea
                    value={flagNote}
                    onChange={(e) => setFlagNote(e.target.value)}
                    className="input-field min-h-[80px]"
                    placeholder="Brief description for other organizations..."
                    data-testid="flag-note-input"
                  />
                </div>
                <div className="flex gap-3">
                  <button onClick={handleToggleFlag} className="flex-1 bg-red-600 hover:bg-red-700 text-white py-3 rounded-lg font-medium" data-testid="flag-place-submit">
                    Place Alert
                  </button>
                  <button onClick={() => setFlagModal(null)} className="px-6 py-3 rounded-lg border-2 border-[#E9DDCF] text-[#666] font-medium">Cancel</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Manual Hours Modal */}
      <ManualHoursModal
        isOpen={showManualHours}
        onClose={() => setShowManualHours(false)}
        onSaved={() => { fetchManualEntries(); fetchEntries(); }}
        scope="partner"
        partnerOrgId={user?.partner_org_id}
      />
    </div>
  );
};

export default TimeEntryApproval;
